// Today's birthdays, shown once per person per day on their first page of
// the day. Nothing shows on a day without one. If it is the signed-in
// person's own birthday they get their own greeting; colleagues see their
// name, never their age (lib/birthdays.js).
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useRole } from './RoleGuard';
import { loadBirthdays, birthdaySub, popupSeenToday, markPopupSeen } from '../lib/birthdays';

const initials = (name) => name.split(',').map(s => s.trim()[0] || '').reverse().join('').toUpperCase();

export default function BirthdayPopup() {
  const { profile } = useAuth();
  const { isCoordinator } = useRole();
  const navigate = useNavigate();
  const [todays, setTodays] = useState(null);

  useEffect(() => {
    if (!profile?.id || popupSeenToday(profile.id)) return;
    let live = true;
    loadBirthdays().then(({ people, error }) => {
      if (!live || error) return;
      const list = people.filter(p => p.isToday);
      if (list.length) setTodays(list);
    });
    return () => { live = false; };
  }, [profile?.id]);

  if (!todays) return null;

  const close = () => { markPopupSeen(profile.id); setTodays(null); };
  const open = (p) => {
    close();
    navigate(p.kind === 'client' ? `/clients/${p.id}` : `/hr/personnel/${p.id}`);
  };
  const mine = todays.find(p => p.profileId && p.profileId === profile.id);
  const others = todays.filter(p => p !== mine);
  const dateLabel = new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && close()}>
      <div className="modal bday-modal" role="dialog" aria-labelledby="bday-title" onMouseDown={e => e.stopPropagation()}>
        <div className="bday-modal-top">
          <div className="bday-modal-eyebrow">{dateLabel}</div>
          <div className="bday-modal-title" id="bday-title">
            {mine ? `Happy birthday, ${mine.firstName}! 🎂` : `🎉 ${todays.length} birthday${todays.length === 1 ? '' : 's'} today`}
          </div>
          {mine && <div className="bday-modal-sub">From everyone at A Heart For Care.</div>}
        </div>
        {others.length > 0 && (
          <div className="bday-modal-body">
            {mine && <div className="bday-modal-also">Also celebrating today</div>}
            {others.map(p => (
              <div key={p.key} className="bday-modal-row">
                <span className={`bday-modal-avatar ${p.kind}`}>{initials(p.name)}</span>
                <div style={{ minWidth: 0 }}>
                  <div className="bday-modal-name">{p.name}</div>
                  <div className="bday-modal-meta">{p.kind === 'client' ? `Client · ${birthdaySub(p)}` : birthdaySub(p)}</div>
                </div>
                {isCoordinator && <button type="button" className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => open(p)}>Open profile</button>}
              </div>
            ))}
          </div>
        )}
        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <span style={{ fontSize: 11.5, color: 'var(--text3)' }}>Also on the dashboard all day</span>
          <button type="button" className="btn btn-primary" onClick={close} autoFocus>Got it</button>
        </div>
      </div>
    </div>
  );
}
